/**
 * Shared Socket.IO connection factory for sl-canvas2 frontend.
 *
 * CRITICAL: All Socket.IO connections outside of nbstore MUST use this factory.
 * nbstore uses its own SocketManager (new Manager()) which creates a separate
 * transport. The io() function from socket.io-client caches Managers globally
 * by host — if any consumer calls io() without forceNew, it creates a cached
 * Manager that competes with nbstore's transport, causing silent connection
 * failures (one connection works, the other hangs).
 *
 * This factory ensures forceNew: true is always set, preventing the conflict.
 * See: fix-socketio-manager-cache-conflict skill.
 */
import {
  io,
  type ManagerOptions,
  type Socket,
  type SocketOptions,
} from 'socket.io-client';

export interface SocketServerLike {
  syncUrl?: string;
  baseUrl?: string;
  waitForConfigRevalidation?: (signal?: AbortSignal) => Promise<void>;
  serverMetadata?: {
    syncUrl?: string;
    baseUrl?: string;
  };
}

function getLiveConfigSyncUrl(server: SocketServerLike): string | undefined {
  return (
    server as SocketServerLike & {
      ['config$']?: {
        value?: {
          syncUrl?: string;
        };
      };
    }
  )['config$']?.value?.syncUrl;
}

/**
 * Create a Socket.IO connection with standard options.
 *
 * @param url - Server URL with optional namespace (e.g. `http://localhost:3010/comment`)
 * @param options - Additional socket.io options (reconnection, etc.)
 */
export function createSocketConnection(
  url: string,
  options?: Partial<ManagerOptions & SocketOptions>
): Socket {
  return io(url, {
    forceNew: true,
    transports: ['websocket', 'polling'],
    withCredentials: true,
    ...options,
  });
}

export function resolveSocketBaseUrl(
  server: SocketServerLike,
  browserOrigin = typeof location === 'undefined' ? undefined : location.origin
): string {
  // Demo cookies are host-only. Routing this browser to the shared sync host
  // would silently turn its Socket.IO handshake into an anonymous request.
  // The Demo host must expose /socket.io itself before public admission opens.
  if (browserOrigin === 'https://demo.streamlain.de') return browserOrigin;
  return (
    server.syncUrl ??
    getLiveConfigSyncUrl(server) ??
    server.serverMetadata?.syncUrl ??
    server.baseUrl ??
    server.serverMetadata?.baseUrl ??
    ''
  );
}

export function hasConfiguredSocketBaseUrl(server: SocketServerLike): boolean {
  return Boolean(
    getLiveConfigSyncUrl(server) ?? server.serverMetadata?.syncUrl
  );
}

export async function ensureSocketServerConfig(
  server: SocketServerLike
): Promise<void> {
  if (hasConfiguredSocketBaseUrl(server)) {
    return;
  }

  await server.waitForConfigRevalidation?.();
}

export function createServerSocketConnection(
  server: SocketServerLike,
  namespace: string,
  options?: Partial<ManagerOptions & SocketOptions>
): Socket {
  const baseUrl = resolveSocketBaseUrl(server);
  if (!baseUrl) {
    throw new Error('Missing Socket.IO server URL');
  }

  const normalizedNamespace = namespace.startsWith('/')
    ? namespace
    : `/${namespace}`;
  return createSocketConnection(`${baseUrl}${normalizedNamespace}`, options);
}
